import {
  filterWorkStationsCategory,
  includeSelectedStation,
  type IDropdownItemStations,
  type RideStationCategory,
} from "@ryde/shared";

/** Active stations the ride can take: not on it yet, unless the ride allows a station twice. */
export const getRideAddableStations = ({
  stations,
  rideStationIds,
  isRideWithDuplicatedStationsAllowed,
}: {
  stations: IDropdownItemStations[] | undefined;
  rideStationIds: number[];
  isRideWithDuplicatedStationsAllowed?: boolean;
}): IDropdownItemStations[] => {
  const activeStations = stations?.filter(station => station?.isActive) ?? [];

  if (isRideWithDuplicatedStationsAllowed) {
    return activeStations;
  }

  const rideStationIdsSet = new Set(rideStationIds);

  return activeStations.filter(
    station => !rideStationIdsSet.has(Number(station.value)),
  );
};

/**
 * The add-station picker: addable stations in the company's work-station categories, plus the selected one.
 * A station created here is neither a destination nor official, so the categories may leave it out.
 */
export const getRideAddStationOptions = ({
  stations,
  rideStationIds,
  isRideWithDuplicatedStationsAllowed,
  stationCategories,
  selectedStationId,
}: {
  stations: IDropdownItemStations[] | undefined;
  rideStationIds: number[];
  isRideWithDuplicatedStationsAllowed?: boolean;
  stationCategories: RideStationCategory[] | undefined;
  selectedStationId: number | undefined;
}): IDropdownItemStations[] => {
  const addableStations = getRideAddableStations({
    stations,
    rideStationIds,
    isRideWithDuplicatedStationsAllowed,
  });

  return includeSelectedStation({
    filteredStations: filterWorkStationsCategory({
      passengers: undefined,
      stationCategories,
      stationsDropdownList: addableStations,
    }),
    selectedStationId,
    allStations: addableStations,
  });
};

/** Whether the picker may select the station: it is in the list and the ride can take it. */
export const isRideAddableStation = ({
  stationId,
  stations,
  rideStationIds,
  isRideWithDuplicatedStationsAllowed,
}: {
  stationId: number;
  stations: IDropdownItemStations[] | undefined;
  rideStationIds: number[];
  isRideWithDuplicatedStationsAllowed?: boolean;
}): boolean =>
  getRideAddableStations({
    stations,
    rideStationIds,
    isRideWithDuplicatedStationsAllowed,
  }).some(station => Number(station.value) === stationId);
